import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { FaCheckCircle, FaExclamationCircle, FaEnvelope, FaSpinner } from 'react-icons/fa';
import { API_URL } from '../config';

// Styled components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
  background-color: #f5f8fa;
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 40px;
  width: 100%;
  max-width: 500px;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 24px;
  color: #333;
  margin-bottom: 20px;
`;

const Message = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 24px;
  line-height: 1.6;
`;

const Icon = styled.div`
  font-size: 64px;
  margin-bottom: 24px;
  color: ${props => props.success ? '#52c41a' : props.error ? '#f5222d' : '#1890ff'};
`;

const Button = styled.button`
  background-color: #1890ff;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 20px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: #40a9ff;
  }
  
  &:disabled {
    background-color: #bfbfbf;
    cursor: not-allowed;
  }
`;

const ResendForm = styled.div`
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #eee;
  text-align: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  margin-bottom: 16px;
  font-size: 16px;
  
  &:focus {
    border-color: #40a9ff;
    outline: none;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`;

const Spinner = styled.span`
  display: inline-block;
  margin-right: 8px;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const AccountActivation = ({ token: propToken }) => {
  // Get token from props or extract from URL if not provided
  const token = propToken || window.location.hash.match(/\/activate\/(.+)/) ? 
    window.location.hash.match(/\/activate\/(.+)/)[1] : null;
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('Activating your account...');
  const [email, setEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendStatus, setResendStatus] = useState(null); // null, success, error
  
  useEffect(() => {
    // If token is provided, activate the account
    if (token) {
      activateAccount(token);
    } else {
      setStatus('error');
      setMessage('No activation token provided. Please check your email for the activation link or request a new one below.');
    }
  }, [token]);
  
  const activateAccount = async (activationToken) => {
    try {
      const response = await axios.get(`${API_URL.replace('/api', '')}/activation/activate/${activationToken}`);
      
      if (response.data.success) {
        setStatus('success');
        setMessage(response.data.message || 'Your account has been successfully activated! You can now log in.');
        
        // If user was automatically logged in, redirect to dashboard after 3 seconds
        if (response.data.user) {
          setTimeout(() => {
            window.location.hash = '#/dashboard';
          }, 3000);
        }
      } else {
        setStatus('error');
        setMessage(response.data.message || 'Failed to activate your account. The activation link may be invalid or expired.');
      }
    } catch (error) {
      setStatus('error');
      setMessage(
        error.response?.data?.message || 
        'An error occurred while activating your account. Please try again or request a new activation link.'
      );
    }
  };
  
  const handleResendActivation = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setResendStatus('error');
      setResendMessage('Please enter your email address');
      return;
    }
    
    try {
      setResendLoading(true);
      setResendStatus(null);
      setResendMessage('');
      
      const response = await axios.post(`${API_URL.replace('/api', '')}/activation/resend`, { email });
      
      if (response.data.success) {
        setResendStatus('success');
        setResendMessage('Activation email sent successfully. Please check your inbox.');
      } else {
        setResendStatus('error');
        setResendMessage(response.data.message || 'Failed to send activation email.');
      }
    } catch (error) {
      setResendStatus('error');
      setResendMessage(
        error.response?.data?.message || 
        'An error occurred while sending the activation email. Please try again later.'
      );
    } finally {
      setResendLoading(false);
    }
  };
  
  return (
    <Container>
      <Card>
        <Icon 
          success={status === 'success'} 
          error={status === 'error'}
        >
          {status === 'loading' && <FaSpinner className="fa-spin" />}
          {status === 'success' && <FaCheckCircle />}
          {status === 'error' && <FaExclamationCircle />}
        </Icon>
        
        <Title>
          {status === 'loading' && 'Account Activation'}
          {status === 'success' && 'Account Activated!'}
          {status === 'error' && 'Activation Failed'}
        </Title>
        
        <Message>{message}</Message>
        
        {status === 'success' && (
          <Button onClick={() => window.location.hash = '#/login'}>
            Go to Login
          </Button>
        )}
        
        {status === 'error' && (
          <ResendForm>
            <Title style={{ fontSize: '18px' }}>
              <FaEnvelope style={{ marginRight: '8px' }} />
              Request New Activation Link
            </Title>
            
            <Message>
              Enter your email address below to receive a new activation link.
            </Message>
            
            <form onSubmit={handleResendActivation}>
              <Input
                type="email"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              
              {resendStatus && (
                <Message style={{ 
                  color: resendStatus === 'success' ? '#52c41a' : '#f5222d',
                  marginTop: '0'
                }}>
                  {resendMessage}
                </Message>
              )}
              
              <Button type="submit" disabled={resendLoading}>
                {resendLoading && <Spinner><FaSpinner /></Spinner>}
                {resendLoading ? 'Sending...' : 'Send Activation Link'}
              </Button>
            </form>
          </ResendForm>
        )}
      </Card>
    </Container>
  );
};

export default AccountActivation;
